import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useArtists } from '../../contexts/ArtistsContext';

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
`;

const SummaryBar = styled.div`
  display: flex;
  gap: var(--space-sm);
  flex-wrap: wrap;
  align-items: center;
`;

const SummaryPill = styled.span`
  background: var(--color-surface);
  color: var(--color-accent);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-md);
`;

const InviteCard = styled.article`
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  padding: var(--space-lg);
  background: white;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const InviteHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InviteName = styled.h2`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  margin: 0;
`;

const InviteEmail = styled.a`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  word-break: break-all;
`;

const DataList = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px var(--space-sm);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const DataLabel = styled.dt`
  font-weight: var(--font-weight-semibold);
`;

const DataValue = styled.dd`
  margin: 0;
  color: var(--color-text);
`;

const formatTemplateName = (template) => {
  if (template === 'personalised') {
    return 'Personalised outreach';
  }

  return 'Standard invite';
};

const Reminder = styled.div`
  padding: var(--space-md);
  border: 1px dashed var(--color-border);
  border-radius: var(--border-radius-md);
  background: rgba(255, 255, 255, 0.68);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const EmptyState = styled.div`
  padding: var(--space-xl);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
`;

const SentInvites = () => {
  const { invites, loadingInvites: loading } = useArtists();

  const totalInvites = invites.length;
  const oldestRequest = useMemo(() => {
    if (invites.length === 0) return null;
    const sorted = [...invites].sort(
      (a, b) => new Date(a.requestedAt) - new Date(b.requestedAt)
    );
    return sorted[0]?.requestedAt ?? null;
  }, [invites]);

  const formatDate = (value) =>
    new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date(value));

  return (
    <PageContainer>
      <Header>
        <Title>Sent Invites</Title>
        <Subtitle>Track artists who have been invited to join the platform.</Subtitle>
      </Header>

      <SummaryBar>
        <SummaryPill>{totalInvites} sent</SummaryPill>
        {oldestRequest && (
          <SummaryPill>Oldest: {formatDate(oldestRequest)}</SummaryPill>
        )}
      </SummaryBar>

      {loading ? (
        <EmptyState>Loading invites...</EmptyState>
      ) : invites.length === 0 ? (
        <EmptyState>No sent invites found. Go to 'Invite Artist' to send one.</EmptyState>
      ) : (
        <CardGrid>
          {invites.map((invite) => (
            <InviteCard key={invite.id}>
              <InviteHeader>
                <InviteName>{invite.name}</InviteName>
                <InviteEmail href={`mailto:${invite.email}`}>{invite.email}</InviteEmail>
              </InviteHeader>
              <DataList>
                <DataLabel>Sent</DataLabel>
                <DataValue>{formatDate(invite.requestedAt)}</DataValue>
                <DataLabel>Template</DataLabel>
                <DataValue>{formatTemplateName(invite.emailTemplate)}</DataValue>
              </DataList>
            </InviteCard>
          ))}
        </CardGrid>
      )}

      <Reminder>
        These are real invites stored in Firestore.
      </Reminder>
    </PageContainer>
  );
};

export default SentInvites;
